import { useEffect, useState, type FormEvent } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import type { SportPublic, SchoolPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

// 151 -- full registration intake (staff-entered 2026 registration form):
// child + health + club + one/two guardians, composed in one submission.

interface GuardianForm {
  name: string;
  email: string;
  phone: string;
}

export default function RegistrationIntakePage() {
  const { schoolId } = useParams<{ schoolId: string }>();
  const navigate = useNavigate();

  const [schoolName, setSchoolName] = useState("");
  const [sports, setSports] = useState<{ id: string; name: string }[]>([]);
  const [clubs, setClubs] = useState<SchoolPublic[]>([]);

  // child
  const [firstName, setFirstName] = useState("");
  const [surname, setSurname] = useState("");
  const [dob, setDob] = useState("");
  const [grade, setGrade] = useState("");
  // health
  const [medicalConditions, setMedicalConditions] = useState("");
  const [allergies, setAllergies] = useState("");
  const [hasMedicalAid, setHasMedicalAid] = useState(false);
  const [medicalAidName, setMedicalAidName] = useState("");
  const [medicalAidNumber, setMedicalAidNumber] = useState("");
  const [emergencyName, setEmergencyName] = useState("");
  const [emergencyPhone, setEmergencyPhone] = useState("");
  // sport + club
  const [sportId, setSportId] = useState("");
  const [clubId, setClubId] = useState("");
  // guardians
  const [g1, setG1] = useState<GuardianForm>({ name: "", email: "", phone: "" });
  const [g2, setG2] = useState<GuardianForm>({ name: "", email: "", phone: "" });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ player_id: string; family_code: string; guardian_invites: { email: string }[] } | null>(null);

  useEffect(() => {
    if (!schoolId) return;
    api.getSchool(schoolId).then((r) => setSchoolName(r.school.name)).catch(() => {});
    api.getSchoolSports(schoolId).then((r) => setSports(r.sports.map((s) => ({ id: s.sport_id, name: s.sport_name })))).catch(() => {});
    api.getSchools().then((r) => setClubs(r.schools.filter((s) => s.venue_type === "club"))).catch(() => {});
  }, [schoolId]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!schoolId) return;
    if (!firstName.trim() && !surname.trim()) {
      setError("At least one of child first name or surname is required");
      return;
    }
    if (!g1.name.trim() || !g1.email.trim()) {
      setError("The first guardian's name and email are required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const guardians = [{ name: g1.name.trim(), email: g1.email.trim(), phone: g1.phone.trim() || null }];
      if (g2.name.trim() && g2.email.trim()) {
        guardians.push({ name: g2.name.trim(), email: g2.email.trim(), phone: g2.phone.trim() || null });
      }
      const res = await api.registrationIntake(schoolId, {
        child: {
          first_name: firstName.trim() || null,
          surname: surname.trim() || null,
          date_of_birth: dob,
          grade: grade ? parseInt(grade, 10) : null,
        },
        health: {
          medical_conditions: medicalConditions.trim() || null,
          allergies: allergies.trim() || null,
          has_medical_aid: hasMedicalAid,
          medical_aid_name: medicalAidName.trim() || null,
          medical_aid_number: medicalAidNumber.trim() || null,
          emergency_contact_name: emergencyName.trim() || null,
          emergency_contact_phone: emergencyPhone.trim() || null,
        },
        sport_id: sportId || null,
        club_id: clubId || null,
        guardians,
      });
      setDone(res);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to register");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="flex flex-col gap-6" data-page="registration-intake" data-state="done">
        <Card className="max-w-lg">
          <CardHeader>
            <CardTitle className="text-base">Registration complete</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="text-sm">The player was registered. Family code <strong>{done.family_code}</strong>.</p>
            <p className="text-sm text-muted-foreground">
              {done.guardian_invites.length} guardian invite(s) created ({done.guardian_invites.map((g) => g.email).join(", ")}).
              They link automatically when the guardian registers with that email.
            </p>
            <div className="flex gap-3">
              <Button onClick={() => navigate(`/players/${done.player_id}/edit`)}>Open player</Button>
              <Button variant="outline" onClick={() => navigate(`/schools/${schoolId}/players`)}>Back to players</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-page="registration-intake" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to={`/schools/${schoolId}/players`} className="hover:underline">{schoolName || "School"} players</Link>
          <span>/</span><span>Register</span>
        </div>
        <h1 className="text-2xl font-semibold">Register a player</h1>
        <p className="text-muted-foreground">Child, health, club and guardian details from the registration form.</p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6" data-form="registration-intake">
        {error && <p className="text-sm text-destructive" data-error>{error}</p>}

        <Card className="max-w-lg">
          <CardHeader><CardTitle className="text-base">Child</CardTitle></CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="first-name">First name</Label>
                <Input id="first-name" data-field="first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="surname">Surname</Label>
                <Input id="surname" data-field="surname" value={surname} onChange={(e) => setSurname(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="dob">Date of birth</Label>
                <Input id="dob" data-field="dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} required />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="grade">Grade</Label>
                <select id="grade" title="Grade" data-field="grade" value={grade} onChange={(e) => setGrade(e.target.value)} className="flex h-10 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <option value="">Not set</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((g) => <option key={g} value={g}>Grade {g}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="sport">Sport (optional)</Label>
                <select id="sport" title="Sport" data-field="sport" value={sportId} onChange={(e) => setSportId(e.target.value)} className="flex h-10 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <option value="">No enrollment</option>
                  {sports.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="club">Club for the season (optional)</Label>
                <select id="club" title="Club" data-field="club" value={clubId} onChange={(e) => setClubId(e.target.value)} disabled={!sportId} className="flex h-10 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60">
                  <option value="">No club</option>
                  {clubs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {!sportId && <p className="text-xs text-muted-foreground">Pick a sport to attach a club.</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="max-w-lg">
          <CardHeader><CardTitle className="text-base">Health & emergency</CardTitle></CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="med-cond">Medical conditions</Label>
              <textarea id="med-cond" data-field="medical-conditions" rows={2} value={medicalConditions} onChange={(e) => setMedicalConditions(e.target.value)} className="flex rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="allergies">Allergies</Label>
              <textarea id="allergies" data-field="allergies" rows={2} value={allergies} onChange={(e) => setAllergies(e.target.value)} className="flex rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
            </div>
            <div className="flex items-center gap-3">
              <input id="has-aid" data-field="has-medical-aid" type="checkbox" checked={hasMedicalAid} onChange={(e) => setHasMedicalAid(e.target.checked)} className="h-4 w-4 rounded border-input" />
              <Label htmlFor="has-aid">Has medical aid</Label>
            </div>
            {hasMedicalAid && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="aid-name">Medical aid name</Label>
                  <Input id="aid-name" data-field="medical-aid-name" value={medicalAidName} onChange={(e) => setMedicalAidName(e.target.value)} />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="aid-number">Medical aid number</Label>
                  <Input id="aid-number" data-field="medical-aid-number" value={medicalAidNumber} onChange={(e) => setMedicalAidNumber(e.target.value)} />
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="emerg-name">Emergency contact name</Label>
                <Input id="emerg-name" data-field="emergency-name" value={emergencyName} onChange={(e) => setEmergencyName(e.target.value)} />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="emerg-phone">Emergency contact phone</Label>
                <Input id="emerg-phone" data-field="emergency-phone" value={emergencyPhone} onChange={(e) => setEmergencyPhone(e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="max-w-lg">
          <CardHeader><CardTitle className="text-base">Guardians</CardTitle></CardHeader>
          <CardContent className="flex flex-col gap-4">
            <GuardianFields label="Guardian 1 (required)" value={g1} onChange={setG1} idPrefix="g1" />
            <GuardianFields label="Guardian 2 (optional)" value={g2} onChange={setG2} idPrefix="g2" />
          </CardContent>
        </Card>

        <div className="flex max-w-lg gap-3">
          <Button type="submit" data-action="submit" disabled={submitting}>{submitting ? "Registering…" : "Register player"}</Button>
          <Button type="button" variant="outline" onClick={() => navigate(`/schools/${schoolId}/players`)}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}

function GuardianFields({ label, value, onChange, idPrefix }: { label: string; value: GuardianForm; onChange: (v: GuardianForm) => void; idPrefix: string }) {
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border p-3">
      <p className="text-sm font-medium">{label}</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="flex flex-col gap-2">
          <Label htmlFor={`${idPrefix}-name`}>Name</Label>
          <Input id={`${idPrefix}-name`} data-field={`${idPrefix}-name`} value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor={`${idPrefix}-email`}>Email</Label>
          <Input id={`${idPrefix}-email`} data-field={`${idPrefix}-email`} type="email" value={value.email} onChange={(e) => onChange({ ...value, email: e.target.value })} />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor={`${idPrefix}-phone`}>Phone</Label>
          <Input id={`${idPrefix}-phone`} data-field={`${idPrefix}-phone`} value={value.phone} onChange={(e) => onChange({ ...value, phone: e.target.value })} />
        </div>
      </div>
    </div>
  );
}
